import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../common/Card';
import { Badge } from '../common/Badge';
import { formatCurrency, shortenAddress } from '../../utils/formatters';
import { Fire, FishSimple } from '@phosphor-icons/react';

export function SmartMoneyPositions({ positions, accessStatus }) {
  const { t } = useTranslation();
  const [lastUpdate] = useState(new Date().toLocaleTimeString());
  const isAuthRequired = accessStatus?.isAuthRequired;
  const isMembershipRequired = accessStatus?.isMembershipRequired;

  if (!positions && (isAuthRequired || isMembershipRequired)) {
    const emptyState = isAuthRequired
      ? { text: t('auth.loginRequired', '请先登录'), className: 'text-accent' }
      : { text: t('membership.noAccess', '没有会员权限'), className: 'text-accent-warm' };
    return (
      <Card className="h-full flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-accent flex items-center gap-2">
            <Fire size={20} weight="duotone" className="text-accent-warm" /> {t('smartMoney.curatedPositions')}
          </h3>
          <div className="flex items-center gap-2 text-xs">
            <Badge variant="positive">{t('smartMoney.realtime')}</Badge>
            <span className="text-gray-400">{lastUpdate}</span>
            <button className="text-gray-400 hover:text-white transition-colors">⟳</button>
          </div>
        </div>
        <div className={`flex-1 flex items-center justify-center ${emptyState.className} text-sm text-center`}>
          {emptyState.text}
        </div>
      </Card>
    );
  }

  // Handle both old and new data structures
  const posData = positions?.positions || positions;
  const yesPositions = posData?.yes || [];
  const noPositions = posData?.no || [];
  const hasPositions = yesPositions.length > 0 || noPositions.length > 0;

  if (!hasPositions) {
    return (
      <Card className="h-full flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-accent flex items-center gap-2">
            <Fire size={20} weight="duotone" className="text-accent-warm" /> {t('smartMoney.curatedPositions')}
          </h3>
          <div className="flex items-center gap-2 text-xs">
            <Badge variant="positive">{t('smartMoney.realtime')}</Badge>
            <span className="text-gray-400">{lastUpdate}</span>
            <button className="text-gray-400 hover:text-white transition-colors">⟳</button>
          </div>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <FishSimple size={48} weight="duotone" className="mx-auto mb-3 text-accent" />
          <div className="text-gray-400">{t('smartMoney.noActivity')}</div>
          <div className="text-sm text-gray-500 mt-2">{t('smartMoney.waitingForWhales')}</div>
        </div>
      </Card>
    );
  }

  const totalYes = positions?.yesTotal || yesPositions.reduce((sum, p) => sum + (p.currentValue || p.size || 0), 0);
  const totalNo = positions?.noTotal || noPositions.reduce((sum, p) => sum + (p.currentValue || p.size || 0), 0);

  return (
    <Card className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-accent flex items-center gap-2">
          <Fire size={20} weight="duotone" className="text-accent-warm" /> {t('smartMoney.curatedPositions')}
        </h3>
        <div className="flex items-center gap-2 text-xs">
          <Badge variant="positive">{t('smartMoney.realtime')}</Badge>
          <span className="text-gray-400">{lastUpdate}</span>
          <button className="text-gray-400 hover:text-white transition-colors">⟳</button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-surface-highlight/60 rounded-lg p-3 text-center">
          <div className="text-xs text-gray-400">Total</div>
          <div className="font-bold">{formatCurrency(totalYes + totalNo)}</div>
        </div>
        <div className="bg-surface-highlight/60 rounded-lg p-3 text-center">
          <div className="text-xs text-gray-400">YES</div>
          <div className="font-bold text-positive">{formatCurrency(totalYes)}</div>
        </div>
        <div className="bg-surface-highlight/60 rounded-lg p-3 text-center">
          <div className="text-xs text-gray-400">NO</div>
          <div className="font-bold text-negative">{formatCurrency(totalNo)}</div>
        </div>
      </div>

      {/* Position Cards */}
      <div className="space-y-3 flex-1 min-h-0 overflow-auto scrollbar-thin pr-1">
        {/* YES Positions */}
        {yesPositions.map((pos, i) => (
          <PositionCard key={`yes-${i}`} position={pos} side="YES" />
        ))}
        
        {/* NO Positions */}
        {noPositions.map((pos, i) => (
          <PositionCard key={`no-${i}`} position={pos} side="NO" />
        ))}
      </div>
    </Card>
  );
}

function PositionCard({ position, side }) {
  const { label, address, outcome, size, avgPrice, curPrice, currentValue, pnl } = position;
  
  const pnlValue = pnl || (currentValue && size && avgPrice ? currentValue - (size * avgPrice) : 0);
  const pnlPercent = avgPrice && curPrice ? ((curPrice - avgPrice) / avgPrice * 100) : 0;

  return (
    <div className="bg-surface-highlight/50 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {/* Trader Label */}
          <span className="text-lg">
            {label === '机器人' ? '🤖' : label === '体育游戏' ? '🎮' : '🐋'}
          </span>
          <div>
            <div className="font-medium text-sm">{label || 'Smart Trader'}</div>
            <div className="text-xs text-gray-500">{shortenAddress(address)}</div>
          </div>
        </div>
        <Badge variant={side === 'YES' ? 'win' : 'loss'}>
          {outcome || side}
        </Badge>
      </div>

      <div className="grid grid-cols-4 gap-2 text-xs">
        <div>
          <div className="text-gray-500">Size</div>
          <div className="font-medium">{size?.toFixed(2) || '-'}</div>
        </div>
        <div>
          <div className="text-gray-500">Avg Price</div>
          <div className="font-medium">{avgPrice ? `$${avgPrice.toFixed(2)}` : '-'}</div>
        </div>
        <div>
          <div className="text-gray-500">Current</div>
          <div className="font-medium">{curPrice ? `$${curPrice.toFixed(2)}` : '-'}</div>
        </div>
        <div>
          <div className="text-gray-500">P&L</div>
          <div className={`font-medium ${pnlValue >= 0 ? 'text-positive' : 'text-negative'}`}>
            {pnlValue >= 0 ? '+' : ''}{formatCurrency(pnlValue)}
          </div>
        </div>
      </div>

      {/* Value bar */}
      <div className="mt-2 pt-2 border-t border-glass-border">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500">Value</span>
          <span className="font-medium">{formatCurrency(currentValue || 0)}</span>
        </div>
      </div>
    </div>
  );
}
